import { Drawer, DrawerProps, styled, useTheme, useMediaQuery } from '@mui/material';

import { useIMCContext } from 'modules/IMC/common/context';
import { useMember } from 'modules/common/queries/member';

const HEADER_HEIGHT_SM = 49;
const HEADER_HEIGHT = 57;
export const RIGHT_DRAWER_WIDTH = 390;
/** Gap between the drawer paper and the viewport edges on desktop. */
const DRAWER_INSET = 8;

const StyledDrawer = styled(Drawer)((props) => {
    // for temporary drawers, we want to show them on top of the page and disable the rest of the page
    return props.variant === 'temporary'
        ? {
              zIndex: props.theme.zIndex.modal + 1,
              '& .MuiDrawer-paper': {
                  overflow: 'hidden',
                  height: `calc(100% - ${HEADER_HEIGHT_SM}px)`,
                  top: 'auto',
                  bottom: 0,
                  right: 0,
                  backgroundColor: 'transparent',
                  borderRadius: 0,
                  boxShadow: props.theme.shadows[18],
                  [props.theme.breakpoints.up('md')]: {
                      height: `calc(100% - ${HEADER_HEIGHT}px)`,
                      left: 'auto',
                      top: 'auto',
                      width: RIGHT_DRAWER_WIDTH,
                  },
              },
          }
        : {
              zIndex: props.theme.zIndex.modal + 1,
              '& .MuiDrawer-paper': {
                  overflow: 'hidden',
                  top: 0,
                  bottom: 0,
                  height: 'auto',
                  backgroundColor: 'transparent',
                  borderRadius: 0,
                  boxShadow: 'none',
                  [props.theme.breakpoints.up('md')]: {
                      top: DRAWER_INSET,
                      bottom: DRAWER_INSET,
                      height: 'auto',
                      left: 'auto',
                      width: RIGHT_DRAWER_WIDTH,
                      borderRadius: 12,
                      marginRight: 8,
                  },
              },
          };
});

interface RightSidebarDrawerHookProps {
    /**
     * Set `enabled: false` on surfaces that render logged out. `/members/` needs an auth token and
     * is not 401-exempt, so an unauthenticated call force-logs the visitor out. Without the member
     * the backdrop defaults to hidden, so pass `hideBackdrop` explicitly if you want one.
     */
    member?: { enabled?: boolean };
}

interface RightSidebarDrawerProps extends DrawerProps {
    open: boolean;
    children: React.ReactNode;
    ariaLabel: string;
    /** Per-hook options for the data this drawer reads on its own. */
    hookProps?: RightSidebarDrawerHookProps;
}

export default function RightSidebarDrawer({
    children,
    open,
    ariaLabel,
    hookProps,
    ...props
}: RightSidebarDrawerProps) {
    // Read, not require: the funnel mounts this drawer outside the member app, where the only
    // thing the context feeds — the open/close transition heuristic — has no meaning anyway.
    const { currOpenDrawer = null, prevOpenDrawer = null } = useIMCContext({ optional: true }) ?? {};
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const { data: member } = useMember({ enabled: hookProps?.member?.enabled ?? true });

    const shouldTransition =
        (prevOpenDrawer === null && currOpenDrawer !== null) || // Opening from no drawer
        (prevOpenDrawer !== null && currOpenDrawer === null); // Closing to no drawer

    return (
        <StyledDrawer
            anchor={isMobile ? 'bottom' : 'right'}
            hideBackdrop={!member?.hasPaymentIssue}
            // Backdrop hidden means the page behind stays clickable, so this must not hold focus
            // either: MUI gives the focus trap to the top modal, and it pulls focus back on every
            // focusin. A member typing into a dialog underneath would land every keystroke nowhere.
            // Kept on the same condition, so the payment-issue drawer still traps as a real block.
            disableEnforceFocus={!member?.hasPaymentIssue}
            transitionDuration={shouldTransition ? 400 : 0}
            ModalProps={{
                keepMounted: false,
            }}
            aria-label={ariaLabel}
            open={open}
            {...props}>
            {children}
        </StyledDrawer>
    );
}
